import React, { useCallback, useEffect, useState } from 'react';
import { getEquipments, createBranchEquipment, updateBranchEquipment, deleteBranchEquipment } from '../services/equipmentService';
import EquipmentCatalogModal from './EquipmentCatalogModal';

export default function EquipoBranchModal({ branchId, existing, onClose, onSaved }) {
  const isEditing = Boolean(existing);

  const [equipments, setEquipments] = useState([]);
  const [equipmentId, setEquipmentId] = useState('');
  const [quantity, setQuantity] = useState(existing ? String(existing.quantity ?? 0) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCatalog, setShowCatalog] = useState(false);

  const loadEquipments = useCallback(() => {
    getEquipments()
      .then(data => setEquipments(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) loadEquipments();
  }, [isEditing, loadEquipments]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (isEditing) {
        await updateBranchEquipment(existing.id, { quantity: Number(quantity) || 0 });
      } else {
        await createBranchEquipment({
          equipmentId: Number(equipmentId),
          branchId,
          quantity: Number(quantity) || 0,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo guardar el equipo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setError('');
    setSaving(true);
    try {
      await deleteBranchEquipment(existing.id);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo eliminar el equipo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 480, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: 'var(--orig-green)' }} />
            {isEditing ? 'Editar Equipo' : 'Agregar Equipo al Inventario'}
          </h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="section-body">
            {error && (
              <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            )}

            <div className="row g-3">
              {isEditing ? (
                <div className="col-12">
                  <div style={{
                    padding: '10px 14px', background: 'var(--orig-panel)',
                    borderRadius: 'var(--orig-radius)', border: '1px solid var(--orig-border)',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--orig-text)' }}>
                      {existing.equipment?.name}
                    </div>
                    {(existing.equipment?.type || existing.equipment?.brand) && (
                      <div style={{ fontSize: 12, color: 'var(--orig-text-muted)' }}>
                        {[existing.equipment?.type, existing.equipment?.brand].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Equipo
                    <button
                      type="button"
                      onClick={() => setShowCatalog(true)}
                      title="Administrar equipos"
                      style={{
                        background: 'var(--orig-green-dim)',
                        border: '1px solid rgba(5,150,105,0.35)',
                        borderRadius: 6, cursor: 'pointer', width: 22, height: 22,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--orig-green-bright)', fontSize: 12, lineHeight: 1,
                      }}
                    >
                      <i className="bi bi-gear-fill" />
                    </button>
                  </label>
                  <select
                    className="form-select"
                    value={equipmentId}
                    onChange={e => setEquipmentId(e.target.value)}
                    required
                  >
                    <option value="" disabled>Seleccione un equipo</option>
                    {equipments.map(eq => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name}{eq.type ? ` — ${eq.type}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12">
                <label className="form-label">Cantidad</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0"
                  min="0"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 10,
            padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
          }}>
            {isEditing ? (
              <button
                type="button"
                className="btn-orig btn-orig-outline"
                onClick={handleDelete}
                disabled={saving}
                style={{ color: 'var(--orig-red-light)' }}
              >
                <i className="bi bi-trash" />
                Eliminar
              </button>
            ) : <span />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn-orig btn-orig-outline" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
                <i className="bi bi-check-lg" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {showCatalog && (
        <EquipmentCatalogModal
          items={equipments}
          onClose={() => setShowCatalog(false)}
          onChange={loadEquipments}
        />
      )}
    </div>
  );
}
